import { useState } from "react";
import { siteConfig, aboutInfo } from "@/mocks/siteData";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `关于我们 - ${siteConfig.siteName}`,
    description: `了解${siteConfig.siteName}的品牌故事、平台信誉和联系方式`,
    url: `${siteConfig.siteUrl}/about`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.siteUrl },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${siteConfig.siteUrl}/about` },
      ],
    },
    about: {
      "@type": "Organization",
      name: siteConfig.siteName,
      description: aboutInfo.brandIntro.content[0],
      email: siteConfig.contactEmail,
      telephone: siteConfig.contactPhone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "建国路88号爱情岛大厦",
        addressLocality: "北京",
        addressRegion: "北京",
        addressCountry: "CN",
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("phone_alt") as string || "").trim();
    if (honeypotValue) {
      setFormStatus("success");
      form.reset();
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9q12qvnahb7382ov4d0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const errorMsg = typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试";
        setFormError(errorMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <PageMeta
        title={`关于我们 - ${siteConfig.siteName}`}
        description={`了解${siteConfig.siteName}的品牌故事、平台信誉和联系方式。国内领先的情感交流与视频内容综合平台。`}
        keywords={`关于爱情岛论坛,${siteConfig.siteName}品牌,爱情岛论坛联系方式`}
        canonicalUrl={`${siteConfig.siteUrl}/about`}
        ogTitle={`关于我们 - ${siteConfig.siteName}`}
        ogDescription={`了解${siteConfig.siteName}的品牌故事、平台信誉和联系方式。`}
        ogType="website"
        ogUrl={`${siteConfig.siteUrl}/about`}
        jsonLd={[aboutSchema]}
      />

      <main className="min-h-screen bg-background-50 pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* 品牌介绍 */}
          <section className="mb-16">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">{aboutInfo.brandIntro.title}</h1>
              <div className="w-12 h-1 bg-primary-500 mx-auto mb-6 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                {aboutInfo.brandIntro.content.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20bright%20office%20space%20with%20young%20creative%20team%20collaborating%2C%20warm%20natural%20light%20streaming%20through%20large%20windows%2C%20minimalist%20design%2C%20plants%20decorating%20the%20space%2C%20friendly%20atmosphere%2C%20professional%20yet%20casual%20vibe&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="爱情岛论坛团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              {aboutInfo.brandIntro.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* 平台信誉 */}
          <section className="mb-16 py-12 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-3xl mx-auto text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{aboutInfo.reputation.title}</h2>
                <p className="text-sm text-foreground-500">用心经营，值得信赖</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {aboutInfo.reputation.items.map((item) => (
                  <div key={item.title} className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/60 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 联系我们 */}
          <section className="mb-12">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">{aboutInfo.contact.title}</h2>
              <p className="text-sm text-foreground-500">{aboutInfo.contact.description}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h4>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h4>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <div className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg">
                    <i className="ri-wechat-line text-lg text-green-600"></i>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg">
                    <i className="ri-weibo-line text-lg text-red-500"></i>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.weibo}</span>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6">
                <h3 className="text-lg font-semibold text-foreground-950 mb-4">发送消息</h3>
                <form className="flex flex-col gap-3" onSubmit={handleSubmit} data-readdy-form="true">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="px-3 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="px-3 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      required
                    />
                  </div>
                  <input
                    type="text"
                    name="subject"
                    placeholder="主题"
                    className="px-3 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    required
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的消息（最多500字）"
                    rows={4}
                    maxLength={500}
                    className="px-3 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    required
                  ></textarea>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="phone-alt-field"
                  />

                  {formStatus === "error" && formError && (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      {formError}
                    </div>
                  )}

                  {formStatus === "success" && (
                    <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                      消息已成功发送！我们会尽快回复您。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === "submitting" ? "发送中..." : "发送消息"}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}